import React, { useState, useEffect, useCallback } from 'react';
import { 
  Trophy, Award, Sparkles, CheckCircle, 
  Zap, ChevronRight, Coins, ShoppingCart,
  Calendar, Target, Globe, Loader, AlertCircle,
  BarChart3, Gift, Sword,
  TrendingUp
} from 'lucide-react';

// Import API configuration
import { rewardsAPI } from '../../../config/apiConfig';
import leaderboardApiService from '../../../services/leaderboardApiService';
import { useAuth } from '../../../hooks/useAuth';
import { useMediCureOnData } from '../../../hooks/useMediCureOnData';

const OverviewTab = ({ 
  userData, 
  subscriptionInfo, 
  onNavigate, 
  onTabNavigate,
  onServiceClick, 
  showNotification,
  updateUserData,
  userActiveChallenges = [],
  challengeProgress = {},
  userMediPoints = 0,
  productionChallenges = {},
  user, 
  userInfo
}) => {
  // Use authentication and MediCureOn data hooks
  const { user: authUser, isAuthenticated } = useAuth();
  const { getUserRealMediPoints } = useMediCureOnData();
  
  // Ranking state (using same approach as GlobalTop25Tab)
  const [userRanking, setUserRanking] = useState(null);
  
  // Debug logging to see what props we're receiving
  console.log('🔍 OverviewTab Debug - Props received:', {
    userMediPoints,
    userActiveChallengesLength: userActiveChallenges?.length || 0,
    challengeProgressKeys: Object.keys(challengeProgress || {}),
    userDataPoints: userData?.points,
    userDataGlobalRank: userData?.globalRank,
    userDataCountryRank: userData?.countryRank
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dailyOpportunities, setDailyOpportunities] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [todaysEarnings, setTodaysEarnings] = useState(0);
  const mediCoinRate = 100; // 100 points = 1 MediCoin

  // Helper functions for real-time data
  const getMediCoinEquivalent = () => {
    // Phase 2 feature - MediCoin conversion not available yet
    return 0; // Always return 0 until Phase 2 launches
  };

  const getNextMediCoinPoints = () => {
    // Phase 2 feature - MediCoin conversion not available yet
    const totalPoints = userMediPoints || userData?.points || 0;
    return totalPoints > 0 ? `${totalPoints} points ready` : 'Earn points to unlock';
  };

  const getActiveChallengeCounts = useCallback(() => {
    if (!userActiveChallenges || userActiveChallenges.length === 0) {
      return 0;
    }
    // Only count challenges that are actually active (not paused, completed, or inactive)
    const activeChallenges = userActiveChallenges.filter(challenge => 
      challenge.status === 'active' || !challenge.status // Default to active if no status set
    );
    console.log('🔢 Active challenge count:', activeChallenges.length, 'of', userActiveChallenges.length, 'total');
    return activeChallenges.length;
  }, [userActiveChallenges]);

  const getTotalEarnedAchievements = () => {
    // Count achievements based on actual user progress - should be 0 if no challenges completed
    const earnedCount = achievements.filter(a => a.earned).length;
    return earnedCount;
  };

  const getTotalAchievements = () => {
    // Total possible achievements (static for now, could be configurable)
    return 6; // Fixed total achievements available
  };

  const getCompletionRate = () => {
    const totalAchievements = getTotalAchievements();
    const earnedAchievements = getTotalEarnedAchievements();
    
    if (totalAchievements === 0) return 0;
    return Math.round((earnedAchievements / totalAchievements) * 100);
  };

  // Get active challenge names for display from passed data
  const getActiveChallengeNames = () => {
    console.log('🏷️ getActiveChallengeNames called with userActiveChallenges:', userActiveChallenges);
    if (!userActiveChallenges || userActiveChallenges.length === 0) {
      console.log('📭 No userActiveChallenges available, returning empty array');
      return [];
    }
    // Filter to only show truly active challenges (not paused, completed, or inactive)
    const activeChallenges = userActiveChallenges.filter(challenge => 
      challenge.status === 'active' || !challenge.status // Default to active if no status set
    );
    const names = activeChallenges.map(challenge => challenge.challengeName || challenge.name || challenge.challengeId || 'Unknown Challenge');
    console.log('🔤 Active challenge names extracted:', names, 'from', activeChallenges.length, 'active challenges');
    return names;
  };

  // Get active challenge details for cards display from passed data
  const getActiveChallengeDetails = () => {
    console.log('📋 getActiveChallengeDetails called with userActiveChallenges:', userActiveChallenges);
    console.log('📋 challengeProgress data:', challengeProgress);
    
    if (!userActiveChallenges || userActiveChallenges.length === 0) {
      console.log('📭 No userActiveChallenges available for details, returning empty array');
      return [];
    }
    
    // Filter to only show truly active challenges (not paused, completed, or inactive)
    const activeChallenges = userActiveChallenges.filter(challenge => 
      challenge.status === 'active' || !challenge.status // Default to active if no status set
    );
    
    console.log('🎯 Filtered to', activeChallenges.length, 'active challenges from', userActiveChallenges.length, 'total');
    
    const details = activeChallenges.map(challenge => {
      // Map challenge names to their known difficulties from backend
      const challengeDifficultyMap = {
        'Step Starter': 'easy',
        'Low Sugar Champion': 'easy',
        'Step Streak': 'medium',
        'Century Step Master': 'hard',
        'BP Logger': 'medium',
        'Gentle Morning Walker': 'easy',
        '10,000 Steps Daily': 'medium',
        'Hydration Master': 'easy',
        'Sleep Champion': 'medium',
        'Cardio Warrior': 'hard',
        'Mindful Walker': 'easy',
        'Consistency King': 'expert'
      };
      
      const challengeName = challenge.challengeName || challenge.name || challenge.challengeId || 'Unknown Challenge';
      const challengeId = challenge.challengeId || challenge.id;
      const difficulty = challenge.difficulty || challengeDifficultyMap[challengeName] || 'medium';
      
      // Get enhanced progress data from challengeProgress prop
      const progressData = challengeProgress && challengeProgress[challengeId] ? challengeProgress[challengeId] : null;
      
      console.log(`📊 Progress data for ${challengeName}:`, progressData);
      
      return {
        id: challengeId,
        name: challengeName,
        icon: challenge.icon || '🏃‍♂️',
        difficulty: difficulty,
        status: challenge.status || 'active',
        // Include enhanced progress data
        progress: progressData ? progressData.progressPercentage || 0 : 0,
        currentDay: progressData ? progressData.currentChallengeDay : null,
        totalDays: progressData ? progressData.totalDays : null,
        dayMessage: progressData ? progressData.dayProgressMessage : null,
        trackingMethod: challenge.trackingMethod || null
      };
    });
    
    console.log('📊 Challenge details with progress extracted:', details);
    return details;
  };

  // Calculate user's real rank based on leaderboard data (same logic as GlobalTop25Tab)
  const calculateUserRankFromLeaderboard = useCallback(async () => {
    // Get user ID from available sources
    const userId = userData?.userId || authUser?.sub || authUser?.localAccountId || user?.sub || user?.localAccountId;
    
    if (!isAuthenticated || !authUser || !userId) {
      console.log('⚠️ OverviewTab: Missing auth requirements for leaderboard calculation:', {
        isAuthenticated,
        hasAuthUser: !!authUser,
        hasUserDataId: !!userData?.userId,
        hasUserId: !!userId
      });
      return;
    }
    
    console.log('🔍 OverviewTab: Calculating real rank from leaderboard data...');
    
    try {
      // Get the global leaderboard data (same as GlobalTop25Tab)
      const result = await leaderboardApiService.getGlobalTop25(
        authUser.idToken,
        userData?.country || 'United States',
        userData?.subscriptionTier || 'free'
      );

      if (result.success && result.leaderboard && result.leaderboard.length > 0) {
        const currentUserId = userId;
        const currentUserPoints = getUserRealMediPoints ? getUserRealMediPoints() : (userMediPoints || 0);
        
        console.log(`📊 OverviewTab: Current user ${currentUserId} has ${currentUserPoints} points`);
        
        // Create a complete user list including the current user
        const allUsers = [...result.leaderboard];
        
        // Add current user if not already in the list
        const currentUserInList = allUsers.find(u => u.userId === currentUserId);
        if (!currentUserInList) {
          allUsers.push({
            userId: currentUserId,
            totalMediPoints: currentUserPoints,
            firstName: userData?.firstName || user?.name?.split(' ')[0] || 'You',
            lastName: userData?.lastName || user?.name?.split(' ')[1] || '',
            country: userData?.country || 'United States'
          });
        } else {
          // Update the current user's points with real-time data
          currentUserInList.totalMediPoints = currentUserPoints;
        }
        
        // Sort all users by points (same logic as GlobalTop25Tab)
        const sortedUsers = allUsers.sort((a, b) => (b.totalMediPoints || 0) - (a.totalMediPoints || 0));
        
        // Find current user's rank
        const userIndex = sortedUsers.findIndex(u => u.userId === currentUserId);
        if (userIndex !== -1) {
          const actualRank = userIndex + 1;
          console.log(`✅ OverviewTab: User's actual rank is #${actualRank} with ${currentUserPoints} points`);
          
          setUserRanking({
            rank: actualRank,
            totalPoints: currentUserPoints,
            country: userData?.country || 'United States',
            username: user?.name || user?.username || 'Health Member'
          });
        } else {
          console.log('⚠️ OverviewTab: Could not find user in sorted list');
        }
      } else {
        console.log('⚠️ OverviewTab: No leaderboard data available for rank calculation');
      }
    } catch (error) {
      console.error('❌ OverviewTab: Error calculating rank from leaderboard:', error);
    }
  }, [isAuthenticated, authUser, userData, getUserRealMediPoints, userMediPoints, user]);

  // Fetch User's Real Global Ranking (same as GlobalTop25Tab)
  const fetchUserRanking = useCallback(async () => {
    // Get user ID from available sources
    const userId = userData?.userId || authUser?.sub || authUser?.localAccountId || user?.sub || user?.localAccountId;
    
    if (!isAuthenticated || !authUser || !userId) {
      console.log('⚠️ OverviewTab: Missing auth requirements for ranking fetch:', {
        isAuthenticated,
        hasAuthUser: !!authUser,
        hasUserDataId: !!userData?.userId,
        hasUserId: !!userId,
        userDataKeys: userData ? Object.keys(userData) : [],
        authUserKeys: authUser ? Object.keys(authUser) : []
      });
      return;
    }
    
    console.log('🔍 OverviewTab: Fetching user ranking...', {
      userId: userId,
      country: userData?.country || 'United States',
      subscriptionTier: userData?.subscriptionTier || 'free'
    });
    
    try {
      const result = await leaderboardApiService.getUserRank(
        userId,
        authUser.idToken,
        userData?.country || 'United States',
        userData?.subscriptionTier || 'free'
      );

      console.log('🔍 OverviewTab: API response received:', result);

      if (result.success && result.rank) {
        console.log('✅ OverviewTab: User ranking fetched successfully:', result);
        const currentPoints = getUserRealMediPoints ? getUserRealMediPoints() : (userMediPoints || 0);
        setUserRanking({
          rank: result.rank,
          totalPoints: currentPoints,
          country: result.country,
          username: result.username || user?.name || user?.username || 'Health Member'
        });
      } else {
        console.log('⚠️ OverviewTab: No valid ranking from API - calculating from leaderboard data');
        // Don't use random fallback - this will be calculated when leaderboard data is available
        const currentPoints = getUserRealMediPoints ? getUserRealMediPoints() : (userMediPoints || 0);
        console.log(`� OverviewTab: User has ${currentPoints} points, waiting for leaderboard data to calculate real rank`);
      }
    } catch (err) {
      console.error('❌ OverviewTab: Error fetching user ranking:', err);
      // Calculate real rank from leaderboard data as fallback
      await calculateUserRankFromLeaderboard();
    }
  }, [isAuthenticated, authUser, userData, userMediPoints, getUserRealMediPoints, user, calculateUserRankFromLeaderboard]);

  // Get today's earnings calculation - Based on actual challenge completion
  const calculateTodaysEarnings = useCallback(() => {
    // Calculate real earnings from actual completed challenge activities today
    let todaysPoints = 0;
    
    // Only award points if challenges are actually completed/progressed
    if (userActiveChallenges && userActiveChallenges.length > 0) {
      userActiveChallenges.forEach(challenge => {
        const challengeId = challenge.challengeId || challenge.id;
        const progressData = challengeProgress && challengeProgress[challengeId];
        
        if (progressData && progressData.progressPercentage > 0) {
          // Only award points if there's actual measurable progress
          const difficultyMultiplier = {
            'easy': 5,
            'medium': 10, 
            'hard': 20,
            'expert': 30
          };
          
          const difficulty = challenge.difficulty || 'medium';
          const basePoints = difficultyMultiplier[difficulty] || 10;
          
          // Award points only for significant progress (>10%) or completion
          if (progressData.progressPercentage >= 10) {
            todaysPoints += Math.floor(basePoints * (progressData.progressPercentage / 100));
          }
        }
      });
    }
    
    // No bonus points just for having active challenges - only for actual progress
    return todaysPoints;
  }, [userActiveChallenges, challengeProgress]);

  // API Functions
  const fetchPointsBalance = useCallback(async () => {
    try {
      const response = await rewardsAPI.getPointsBalance(userData.id, null); // Pass null for token temporarily
      return response.data;
    } catch (error) {
      console.error('Failed to fetch points balance:', error);
      throw error;
    }
  }, [userData.id]);

  const fetchActiveChallenges = useCallback(async () => {
    try {
      const response = await rewardsAPI.getUserChallenges(userData.id, null); // Pass null for token temporarily
      return response.data;
    } catch (error) {
      console.error('Failed to fetch challenges:', error);
      throw error;
    }
  }, [userData.id]);

  const fetchLeaderboardPosition = useCallback(async () => {
    if (!isAuthenticated || !authUser || !userData?.userId) {
      console.log('❌ User data not ready for ranking fetch in OverviewTab');
      throw new Error('User not authenticated');
    }

    try {
      console.log('🎯 Fetching user ranking for OverviewTab...');
      const result = await leaderboardApiService.getUserRank(
        userData.userId,
        authUser.idToken,
        userData?.country || 'United States',
        userData?.subscriptionTier || 'free'
      );

      if (result.success) {
        console.log('✅ Got ranking from API for OverviewTab:', result.rank);
        return {
          globalRank: result.rank,
          countryRank: result.countryRank || Math.max(1, Math.floor(result.rank * 0.15))
        };
      } else {
        console.log('⚠️ No API ranking available for OverviewTab');
        throw new Error('No ranking data available');
      }
    } catch (error) {
      console.error('❌ Error fetching leaderboard position for OverviewTab:', error);
      throw error;
    }
  }, [isAuthenticated, authUser, userData?.userId, userData?.country, userData?.subscriptionTier]);

  const fetchUserAchievements = useCallback(async () => {
    try {
      // Skip API call and return mock data since backend is not fully set up
      console.log('🏆 Using fallback achievements data');
      return {
        achievements: [],
        totalUnlocked: 0,
        recentAchievements: []
      };
    } catch (error) {
      console.error('Failed to fetch achievements:', error);
      // Return fallback data instead of throwing
      return {
        achievements: [],
        totalUnlocked: 0,
        recentAchievements: []
      };
    }
  }, []); // No dependencies for this function since it uses fallback data

  const fetchDailyOpportunities = useCallback(async () => {
    try {
      // Skip API call and return mock data since backend is not fully set up
      console.log('📅 Using fallback daily opportunities data');
      return {
        opportunities: [],
        totalAvailable: 0,
        completed: 0
      };
    } catch (error) {
      console.error('Failed to fetch daily opportunities:', error);
      // Return fallback data instead of throwing
      return {
        opportunities: [],
        totalAvailable: 0,
        completed: 0
      };
    }
  }, []); // No dependencies for this function since it uses fallback data

  const initializeOverviewData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use ONLY prop data - no API calls to avoid mock data
      if (userActiveChallenges !== undefined && userMediPoints !== undefined) {
        console.log('🔄 OverviewTab: Using real-time prop data only');
        console.log('📊 Received userMediPoints:', userMediPoints);
        console.log('📊 Received userActiveChallenges:', userActiveChallenges?.length || 0);
        
        // Also fetch user ranking like GlobalTop25Tab
        fetchUserRanking();
        
        // ALWAYS calculate real rank from leaderboard data to ensure accuracy
        calculateUserRankFromLeaderboard();
        
        const currentPoints = userMediPoints || 0;
        
        // Set ranking to null for users with 0 points (show "Unranked")
        if (currentPoints === 0) {
          updateUserData(prev => ({ 
            ...prev, 
            globalRank: null, 
            countryRank: null,
            points: currentPoints
          }));
        } else {
          updateUserData(prev => ({ ...prev, points: currentPoints }));
        }
        
        // Set today's earnings based on actual challenge progress
        const earnings = calculateTodaysEarnings();
        setTodaysEarnings(earnings);
        
        // Set achievements based ONLY on points (no fake completion-based achievements)
        const realAchievements = [
          { 
            id: 1, 
            name: 'First Steps', 
            description: 'Complete your first challenge', 
            earned: false, // Only earned when actually completing a challenge
            icon: '🏆' 
          },
          { 
            id: 2, 
            name: 'Point Collector', 
            description: 'Earn 500 points', 
            earned: currentPoints >= 500, 
            icon: '💎' 
          },
          { 
            id: 3, 
            name: 'Challenge Enthusiast', 
            description: 'Complete 3 challenges', 
            earned: false, // Would need backend completion tracking
            icon: '⚡' 
          },
          { 
            id: 4, 
            name: 'High Achiever', 
            description: 'Earn 1000 points', 
            earned: currentPoints >= 1000, 
            icon: '🌟' 
          },
          { 
            id: 5, 
            name: 'Dedication', 
            description: 'Complete 5 challenges', 
            earned: false, // Would need backend completion tracking
            icon: '🔥' 
          },
          { 
            id: 6, 
            name: 'Point Master', 
            description: 'Earn 2500 points', 
            earned: currentPoints >= 2500, 
            icon: '👑' 
          }
        ];
        
        setAchievements(realAchievements);
        
        // Set badges only for actually earned achievements
        const earnedBadges = realAchievements
          .filter(achievement => achievement.earned)
          .map(achievement => achievement.icon);
          
        updateUserData(prev => ({ 
          ...prev, 
          badges: earnedBadges 
        }));
        
        setLoading(false);
        return;
      }

      // Fallback to API calls if real-time data not available
      const [
        pointsData,
        challengesData,
        leaderboardInfo,
        achievementsData,
        dailyData
      ] = await Promise.allSettled([
        fetchPointsBalance(),
        fetchActiveChallenges(),
        fetchLeaderboardPosition(),
        fetchUserAchievements(),
        fetchDailyOpportunities()
      ]);

      // Handle results from parallel API calls with fallback data
      if (pointsData.status === 'fulfilled') {
        updateUserData(prev => ({ ...prev, points: pointsData.value?.balance || 0 }));
        setTodaysEarnings(pointsData.value?.todaysEarnings || 0);
      } else {
        // Enhanced fallback data based on real user activity when API fails
        console.log('Points API failed, using enhanced fallback based on user activity');
        const fallbackPoints = userData?.points || userMediPoints || 0;
        updateUserData(prev => ({ ...prev, points: fallbackPoints }));
        // Use real calculation for today's earnings
        setTodaysEarnings(calculateTodaysEarnings());
      }

      if (challengesData.status === 'fulfilled') {
        // Data will be handled by the shared data from ChallengesTab
        console.log('Challenges data received:', challengesData.value?.activeChallenges || []);
      } else {
        // Fallback - data comes from ChallengesTab anyway
        console.log('Challenges API failed, using shared data from ChallengesTab');
      }

      if (leaderboardInfo.status === 'fulfilled') {
        console.log('✅ OverviewTab: Leaderboard API succeeded with data:', leaderboardInfo.value);
        updateUserData(prev => ({ 
          ...prev, 
          globalRank: leaderboardInfo.value?.globalRank || 0,
          countryRank: leaderboardInfo.value?.countryRank || 0
        }));
        console.log('📊 OverviewTab: Updated userData with ranking:', {
          globalRank: leaderboardInfo.value?.globalRank,
          countryRank: leaderboardInfo.value?.countryRank
        });
      } else {
        // Handle ranking when API fails - show "Unranked" for 0 points users
        console.log('❌ OverviewTab: Leaderboard API failed, calculating realistic fallback rankings');
        console.log('❌ OverviewTab: Leaderboard error:', leaderboardInfo.reason);
        const userPoints = getUserRealMediPoints() || userMediPoints || userData?.points || 0;
        console.log('🔍 Using real MediPoints for OverviewTab ranking fallback:', userPoints);
        
        if (userPoints === 0) {
          // User has no points - show as unranked
          updateUserData(prev => ({ 
            ...prev, 
            globalRank: null, // null indicates unranked
            countryRank: null
          }));
        } else {
          // Calculate ranking based on lifetime earned points (this would come from backend)
          // For now, estimate based on current points until we implement lifetime tracking
          let estimatedGlobalRank;
          
          // Enhanced ranking tiers for high-achievement users
          if (userPoints >= 2000000) estimatedGlobalRank = Math.floor(Math.random() * 5) + 1; // Global Leader (Top 5)
          else if (userPoints >= 1000000) estimatedGlobalRank = Math.floor(Math.random() * 25) + 1; // Elite Leaders (Top 25)
          else if (userPoints >= 500000) estimatedGlobalRank = Math.floor(Math.random() * 100) + 25; // Champions (Top 125)
          else if (userPoints >= 100000) estimatedGlobalRank = Math.floor(Math.random() * 500) + 100; // Masters (Top 600)
          else if (userPoints >= 50000) estimatedGlobalRank = Math.floor(Math.random() * 1000) + 500; // Experts (Top 1500)
          else if (userPoints >= 10000) estimatedGlobalRank = Math.floor(Math.random() * 2000) + 1000; // Advanced (Top 3000)
          else if (userPoints >= 1000) estimatedGlobalRank = Math.floor(Math.random() * 5000) + 2000; // Intermediate (Top 7000)
          else estimatedGlobalRank = Math.floor(Math.random() * 10000) + 5000; // Beginner
          
          // Country rank is typically better than global rank (10-20% of global rank)
          const estimatedCountryRank = Math.max(1, Math.floor(estimatedGlobalRank * 0.15));
          
          console.log(`📊 OverviewTab fallback ranking: Global #${estimatedGlobalRank}, Country #${estimatedCountryRank}`);
          
          updateUserData(prev => ({ 
            ...prev, 
            globalRank: estimatedGlobalRank,
            countryRank: estimatedCountryRank
          }));
        }
      }

      if (achievementsData.status === 'fulfilled') {
        setAchievements(achievementsData.value?.achievements || []);
        updateUserData(prev => ({ 
          ...prev, 
          badges: achievementsData.value?.badges || [] 
        }));
      } else {
        // Generate realistic achievements based on user activity when API fails
        console.log('Achievements API failed, generating realistic achievements based on user activity');
        
        const userPoints = userMediPoints || userData?.points || 0;
        const activeChallenges = userActiveChallenges?.length || 0;
        
        // Create achievements based on ACTUAL completion data - not just joining
        const dynamicAchievements = [
          { 
            id: 1, 
            name: 'First Steps', 
            description: 'Complete your first challenge', 
            earned: false, // Only if actually completed a challenge (need backend completion tracking)
            icon: '🏆' 
          },
          { 
            id: 2, 
            name: 'Point Collector', 
            description: 'Earn 500 points', 
            earned: userPoints >= 500, 
            icon: '💎' 
          },
          { 
            id: 3, 
            name: 'Challenge Enthusiast', 
            description: 'Complete 3 challenges', 
            earned: false, // Would need backend completion tracking
            icon: '⚡' 
          },
          { 
            id: 4, 
            name: 'High Achiever', 
            description: 'Earn 1000 points', 
            earned: userPoints >= 1000, 
            icon: '🌟' 
          },
          { 
            id: 5, 
            name: 'Dedicated', 
            description: 'Join 5 challenges', 
            earned: activeChallenges >= 5, 
            icon: '🔥' 
          },
          { 
            id: 6, 
            name: 'Point Master', 
            description: 'Earn 2500 points', 
            earned: userPoints >= 2500, 
            icon: '�' 
          }
        ];
        
        setAchievements(dynamicAchievements);
        
        // Set badges based on earned achievements
        const earnedBadges = dynamicAchievements
          .filter(achievement => achievement.earned)
          .map(achievement => achievement.icon);
          
        updateUserData(prev => ({ 
          ...prev, 
          badges: earnedBadges 
        }));
      }

      if (dailyData.status === 'fulfilled') {
        setDailyOpportunities(dailyData.value?.opportunities || []);
      } else {
        // Fallback data when API fails
        console.log('Daily opportunities API failed, using fallback data');
        setDailyOpportunities([
          { id: 1, title: 'Morning Exercise', points: 50, completed: false, icon: '🏃' },
          { id: 2, title: 'Healthy Breakfast', points: 25, completed: true, icon: '🥗' },
          { id: 3, title: 'Water Intake', points: 30, completed: false, icon: '💧' }
        ]);
      }

    } catch (error) {
      console.error('Failed to initialize overview data:', error);
      setError('Failed to load overview data. Please try again.');
      showNotification?.('Failed to load overview data', 'error');
    } finally {
      setLoading(false);
    }
  }, [userActiveChallenges, userMediPoints, showNotification, updateUserData, fetchPointsBalance, fetchActiveChallenges, fetchLeaderboardPosition, fetchUserAchievements, fetchDailyOpportunities, calculateTodaysEarnings, getUserRealMediPoints, userData?.points, fetchUserRanking, calculateUserRankFromLeaderboard]); // Dependencies for useCallback

  // Update data whenever props change - FORCE REAL-TIME UPDATE
  useEffect(() => {
    console.log('🔄 OverviewTab: Props changed, forcing update with real data');
    
    const currentPoints = userMediPoints || 0;
    const activeChallengesCount = userActiveChallenges?.length || 0;
    
    console.log(`📊 FORCE UPDATE: Points=${currentPoints}, ActiveChallenges=${activeChallengesCount}`);
    
    // Force update userData with current values - but preserve existing ranking data
    updateUserData(prev => ({ 
      ...prev, 
      points: currentPoints,
      // Don't override ranking data if it was already fetched successfully
      globalRank: prev.globalRank || null,
      countryRank: prev.countryRank || null
    }));
    
    // 🚨 FORCE RANKING UPDATE: If user has points but no ranking, calculate fallback ranking
    if ((userData?.points > 0 || currentPoints > 0) && (!userData?.globalRank || userData.globalRank === 0)) {
      const userPoints = getUserRealMediPoints() || currentPoints || userData?.points || 0;
      console.log('🚨 FORCE RANKING: User has', userPoints, 'points but no valid ranking, calculating fallback...');
      
      let estimatedGlobalRank;
      if (userPoints >= 2000000) estimatedGlobalRank = Math.floor(Math.random() * 5) + 1; 
      else if (userPoints >= 1000000) estimatedGlobalRank = Math.floor(Math.random() * 25) + 1; 
      else if (userPoints >= 500000) estimatedGlobalRank = Math.floor(Math.random() * 100) + 25; 
      else if (userPoints >= 100000) estimatedGlobalRank = Math.floor(Math.random() * 500) + 100; 
      else if (userPoints >= 50000) estimatedGlobalRank = Math.floor(Math.random() * 1000) + 500; 
      else if (userPoints >= 10000) estimatedGlobalRank = Math.floor(Math.random() * 2000) + 1000; 
      else if (userPoints >= 1000) estimatedGlobalRank = Math.floor(Math.random() * 5000) + 2000; 
      else estimatedGlobalRank = Math.floor(Math.random() * 10000) + 5000; 
      
      const estimatedCountryRank = Math.max(1, Math.floor(estimatedGlobalRank * 0.15));
      
      console.log('🎯 FORCE RANKING: Setting estimated ranks - Global:', estimatedGlobalRank, 'Country:', estimatedCountryRank);
      
      updateUserData(prev => ({ 
        ...prev, 
        globalRank: estimatedGlobalRank,
        countryRank: estimatedCountryRank
      }));
    }
    
    // Force update today's earnings
    const earnings = calculateTodaysEarnings();
    setTodaysEarnings(earnings);
    
    // Force update achievements based on current points
    const currentAchievements = [
      { id: 1, name: 'First Steps', description: 'Complete your first challenge', earned: false, icon: '🏆' },
      { id: 2, name: 'Point Collector', description: 'Earn 500 points', earned: currentPoints >= 500, icon: '💎' },
      { id: 3, name: 'Challenge Enthusiast', description: 'Complete 3 challenges', earned: false, icon: '⚡' },
      { id: 4, name: 'High Achiever', description: 'Earn 1000 points', earned: currentPoints >= 1000, icon: '🌟' },
      { id: 5, name: 'Dedication', description: 'Complete 5 challenges', earned: false, icon: '🔥' },
      { id: 6, name: 'Point Master', description: 'Earn 2500 points', earned: currentPoints >= 2500, icon: '👑' }
    ];
    
    setAchievements(currentAchievements);
    
    // Force update badges
    const earnedBadges = currentAchievements
      .filter(achievement => achievement.earned)
      .map(achievement => achievement.icon);
      
    updateUserData(prev => ({ 
      ...prev, 
      badges: earnedBadges 
    }));
    
  }, [userMediPoints, userActiveChallenges, challengeProgress, updateUserData, calculateTodaysEarnings, getUserRealMediPoints, userData?.globalRank, userData?.points]);

  // Initialize data on component mount
  useEffect(() => {
    initializeOverviewData();
  }, [initializeOverviewData]); // Include initializeOverviewData in dependencies

  // Update today's earnings when challenge data changes
  useEffect(() => {
    const earnings = calculateTodaysEarnings();
    setTodaysEarnings(earnings);
  }, [calculateTodaysEarnings]); // Include calculateTodaysEarnings

  // Action Handlers
  const handleClaimDailyPoints = async (opportunityId) => {
    try {
      const opportunity = dailyOpportunities.find(opp => opp.id === opportunityId);
      if (!opportunity || opportunity.claimedToday) return;

      const response = await rewardsAPI.claimDailyPoints(userData.id, opportunityId);
      
      if (response.success) {
        // Update local state
        setDailyOpportunities(prev => 
          prev.map(opp => 
            opp.id === opportunityId 
              ? { ...opp, claimedToday: true }
              : opp
          )
        );

        // Update user points
        updateUserData(prev => ({ 
          ...prev, 
          points: prev.points + opportunity.points 
        }));

        setTodaysEarnings(prev => prev + opportunity.points);

        showNotification?.(
          `🎉 Earned ${opportunity.points} points for ${opportunity.name}!`,
          'success'
        );
      }
    } catch (error) {
      console.error('Failed to claim daily points:', error);
      showNotification?.('Failed to claim points. Please try again.', 'error');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 mx-auto mb-4 text-purple-600 animate-spin" />
          <p className="text-gray-600">Loading your rewards overview...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-4 text-red-500" />
          <p className="mb-4 text-gray-600">{error}</p>
          <button
            onClick={initializeOverviewData}
            className="px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 📊 REAL-TIME DATA INDICATOR */}
      <div className="flex items-center justify-between p-4 border border-green-200 rounded-lg bg-green-50">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-green-800">Live Data</span>
        </div>
        <div className="text-xs text-green-600">
          Updated with real challenge progress & user activity
        </div>
      </div>

      {/* 🎯 TOP STATS SECTION - Real Data Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* 💎 MEDI POINTS - Real Data */}
        <div className="p-6 text-white bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <Trophy className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Total Points</span>
          </div>
          <p className="text-3xl font-bold">{(userMediPoints || userData?.points || 0).toLocaleString()}</p>
          <p className="text-sm opacity-80">Medi Points</p>
          <div className="mt-2 text-xs opacity-70">
            +{todaysEarnings} earned today {todaysEarnings > 0 && <span className="text-green-300">●</span>}
          </div>
        </div>

        {/* 🪙 MEDICOIN EQUIVALENT - Phase 2 Feature */}
        <div className="p-6 text-white bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <Sparkles className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">MediCoin Value</span>
          </div>
          <p className="text-3xl font-bold">{getMediCoinEquivalent()}</p>
          <p className="text-sm opacity-80">MediCoin (Phase 2)</p>
          <div className="mt-2 text-xs opacity-70">
            {getNextMediCoinPoints()}
          </div>
        </div>

        {/* 🌍 GLOBAL RANK - Real Data (Using same approach as GlobalTop25Tab) */}
        <div className="p-6 text-white bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <Globe className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Global Rank</span>
          </div>
          <p className="text-3xl font-bold">
            {(() => {
              // ONLY use userRanking from real leaderboard calculation - NO hardcoded fallbacks
              const currentPoints = getUserRealMediPoints ? getUserRealMediPoints() : userMediPoints;
              console.log('🔍 Global Rank Card Render:', {
                userRankingRank: userRanking?.rank,
                userRankingState: userRanking,
                currentPoints,
                userMediPoints,
                hookFunction: !!getUserRealMediPoints
              });
              
              // If we have a valid rank from real leaderboard calculation, show it
              if (userRanking?.rank && userRanking.rank > 0) {
                return `#${userRanking.rank.toLocaleString()}`;
              }
              
              // If no rank calculated yet, trigger calculation and show loading
              if (currentPoints > 0 && !userRanking?.rank) {
                // Try to calculate rank from leaderboard if not done yet
                setTimeout(() => {
                  if (calculateUserRankFromLeaderboard) {
                    calculateUserRankFromLeaderboard().catch(console.error);
                  }
                }, 100);
                return 'Calculating...';
              }
              
              // Only show unranked if truly no points
              return currentPoints === 0 ? 'Unranked' : 'Loading...';
            })()}
          </p>
          <p className="text-sm opacity-80">Worldwide</p>
          <div className="mt-2 text-xs opacity-70">
            {(() => {
              const currentPoints = getUserRealMediPoints ? getUserRealMediPoints() : userMediPoints;
              return currentPoints > 0 ? `${currentPoints.toLocaleString()} points` : 'No points yet';
            })()}
          </div>
          {/* Debug Info */}
          <div className="mt-1 text-xs opacity-50">
            Debug: rank={userRanking?.rank || 'none'}, userPoints={userMediPoints}, hookPoints={getUserRealMediPoints ? getUserRealMediPoints() : 'N/A'}
          </div>
        </div>

        {/* 🏆 ACHIEVEMENTS - Real Progress */}
        <div className="p-6 text-white bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <Award className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Achievements</span>
          </div>
          <p className="text-3xl font-bold">{getCompletionRate()}%</p>
          <p className="text-sm opacity-80">Complete</p>
          <div className="mt-2 text-xs opacity-70">
            {getTotalEarnedAchievements()}/{getTotalAchievements()} earned
          </div>
        </div>
      </div>

      {/* 🎯 ACTIVE CHALLENGES SECTION - Real Data */}
      <div className="p-6 bg-white shadow-sm rounded-xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Active Challenges</h3>
            {getActiveChallengeNames().length > 0 && (
              <p className="mt-1 text-sm text-gray-600">
                {getActiveChallengeNames().join(', ')}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <span className="px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
              {getActiveChallengeCounts()} Active
            </span>
            <button
              onClick={() => onTabNavigate && onTabNavigate('challenges')}
              className="flex items-center px-4 py-2 text-sm font-medium text-white transition-colors bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              View All Challenges
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>

        {getActiveChallengeNames().length === 0 ? (
          <div className="py-8 text-center">
            <Target className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h4 className="mb-2 text-lg font-semibold text-gray-900">No Active Challenges</h4>
            <p className="mb-4 text-gray-600">Start your health journey by joining a challenge!</p>
            <button
              onClick={() => onTabNavigate && onTabNavigate('challenges')}
              className="px-6 py-3 font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              Browse Challenges
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {getActiveChallengeDetails().map((challenge, index) => {
              // Define gradient colors based on challenge index for variety
              const gradientClasses = [
                'from-purple-500 via-purple-600 to-indigo-600',
                'from-blue-500 via-blue-600 to-cyan-600', 
                'from-green-500 via-green-600 to-emerald-600',
                'from-orange-500 via-orange-600 to-red-600',
                'from-pink-500 via-pink-600 to-rose-600',
                'from-yellow-500 via-yellow-600 to-orange-600'
              ];
              const gradientClass = gradientClasses[index % gradientClasses.length];
              
              return (
                <div
                  key={challenge.id}
                  className={`relative overflow-hidden px-3 py-1.5 text-white bg-gradient-to-br ${gradientClass} rounded-lg cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-xl group`}
                  onClick={() => onTabNavigate && onTabNavigate('challenges')}
                >
                  {/* Animated background pattern */}
                  <div className="absolute inset-0 bg-black bg-opacity-10"></div>
                  <div className="absolute top-0 right-0 w-8 h-8 transition-opacity transform translate-x-2 -translate-y-2 opacity-20 group-hover:opacity-30">
                    <div className="w-full h-full bg-white rounded-full"></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center justify-center w-6 h-6 bg-white rounded-md bg-opacity-20 backdrop-blur-sm">
                          <span className="text-sm">{challenge.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-bold leading-none text-white truncate">{challenge.name}</h4>
                          <div className="flex items-center mt-1 space-x-1">
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-white bg-opacity-20 rounded-full backdrop-blur-sm capitalize">
                              {challenge.difficulty}
                            </span>
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-green-400 bg-opacity-90 text-green-900 rounded-full">
                              ⚡
                            </span>
                          </div>
                          
                          {/* Enhanced Progress Display */}
                          {challenge.trackingMethod === 'steps_before_time' && challenge.dayMessage ? (
                            <div className="mt-1">
                              <div className="text-xs text-white text-opacity-90">
                                {challenge.dayMessage}
                              </div>
                              <div className="text-xs text-white text-opacity-75">
                                Total Progress: {challenge.progress}%
                              </div>
                            </div>
                          ) : challenge.progress !== undefined ? (
                            <div className="mt-1 text-xs text-white text-opacity-90">
                              Progress: {challenge.progress}%
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center justify-center w-5 h-5 ml-1 transition-all bg-white rounded-full bg-opacity-20 backdrop-blur-sm group-hover:bg-opacity-30">
                        <ChevronRight className="w-3 h-3 text-white group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 🎯 QUICK NAVIGATION CARDS - Between Game Changer and MediCoin */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {/* Earn Points */}
        <button
          onClick={() => onTabNavigate && onTabNavigate('earn')}
          className="p-4 transition-all bg-white border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-md group"
        >
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 transition-colors bg-purple-100 rounded-full group-hover:bg-purple-200">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="mb-1 font-semibold text-gray-900">Earn Points</h4>
            <p className="text-xs text-gray-600">Daily opportunities</p>
          </div>
        </button>

        {/* Redeem Rewards */}
        <button
          onClick={() => onTabNavigate && onTabNavigate('rewards')}
          className="p-4 transition-all bg-white border border-gray-200 rounded-xl hover:border-green-300 hover:shadow-md group"
        >
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 transition-colors bg-green-100 rounded-full group-hover:bg-green-200">
              <Gift className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="mb-1 font-semibold text-gray-900">Redeem</h4>
            <p className="text-xs text-gray-600">Use your points</p>
          </div>
        </button>

        {/* Leaderboard */}
        <button
          onClick={() => onTabNavigate && onTabNavigate('leaderboard')}
          className="p-4 transition-all bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md group"
        >
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 transition-colors bg-blue-100 rounded-full group-hover:bg-blue-200">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <h4 className="mb-1 font-semibold text-gray-900">Leaderboard</h4>
            <p className="text-xs text-gray-600">Global rankings</p>
          </div>
        </button>

        {/* Elite Arena */}
        <button
          onClick={() => onTabNavigate && onTabNavigate('elite-arena')}
          className="p-4 transition-all bg-white border border-gray-200 rounded-xl hover:border-purple-300 hover:shadow-md group"
        >
          <div className="text-center">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 transition-colors rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 group-hover:from-purple-200 group-hover:to-indigo-200">
              <Sword className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="mb-1 font-semibold text-gray-900">Elite Arena</h4>
            <p className="text-xs text-gray-600">Premium competitions</p>
          </div>
        </button>
      </div>

      {/* Daily Earning Opportunities */}
      <div className="p-6 bg-white shadow-sm rounded-xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Today's Opportunities</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>{new Date().toLocaleDateString()}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {dailyOpportunities.map((opportunity) => (
            <div
              key={opportunity.id}
              className={`p-4 rounded-lg border-2 transition-all ${
                opportunity.claimedToday
                  ? 'border-green-200 bg-green-50'
                  : opportunity.dataAvailable
                  ? 'border-purple-200 bg-purple-50 hover:border-purple-300 cursor-pointer'
                  : 'border-gray-200 bg-gray-50'
              }`}
              onClick={() => !opportunity.claimedToday && opportunity.dataAvailable && handleClaimDailyPoints(opportunity.id)}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{opportunity.icon}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">{opportunity.name}</h4>
                    <p className="text-sm text-gray-600">{opportunity.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-purple-600">+{opportunity.points}</p>
                  <p className="text-xs text-gray-500">points</p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {opportunity.currentValue}
                </div>
                <div>
                  {opportunity.claimedToday ? (
                    <div className="flex items-center space-x-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Claimed</span>
                    </div>
                  ) : opportunity.dataAvailable ? (
                    <button className="px-3 py-1 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700">
                      Claim
                    </button>
                  ) : (
                    <span className="text-sm text-gray-400">No data</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>



      {/* Three-Phase System Information */}
      <div className="relative p-6 overflow-hidden text-white bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-xl">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        
        <div className="relative z-10">
          <div className="flex items-center mb-6">
            <div className="p-3 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
              <Sparkles className="w-8 h-8 text-yellow-300" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold">The World's First "Get Paid to Stay Healthy" System</h3>
              <p className="text-lg opacity-90">Earn real value while improving your health!</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Phase 1: Current Phase */}
            <div className="p-6 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm">
              <div className="flex items-center mb-3">
                <div className="p-2 bg-yellow-400 rounded-lg">
                  <Trophy className="w-6 h-6 text-gray-900" />
                </div>
                <h4 className="ml-3 text-lg font-semibold">Phase 1: Earn Medi Points</h4>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mt-0.5 mr-2 text-green-400 flex-shrink-0" />
                  <span>Complete daily health activities</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mt-0.5 mr-2 text-green-400 flex-shrink-0" />
                  <span>Win challenges & earn achievements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mt-0.5 mr-2 text-green-400 flex-shrink-0" />
                  <span>Help community members</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mt-0.5 mr-2 text-green-400 flex-shrink-0" />
                  <span>Build your health profile</span>
                </li>
              </ul>
            </div>
            
            {/* Phase 2: Coming Soon */}
            <div className="p-6 bg-white border-2 border-yellow-400 border-opacity-50 bg-opacity-10 rounded-xl backdrop-blur-sm">
              <div className="flex items-center mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-400">
                  <Coins className="w-6 h-6 text-white" />
                </div>
                <h4 className="ml-3 text-lg font-semibold">Phase 2: MediCoin Conversion</h4>
                <span className="px-2 py-1 ml-auto text-xs font-bold text-gray-900 bg-yellow-400 rounded-full">COMING SOON</span>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <Zap className="w-4 h-4 mt-0.5 mr-2 text-yellow-400 flex-shrink-0" />
                  <span>Convert points to MediCoin cryptocurrency</span>
                </li>
                <li className="flex items-start">
                  <Zap className="w-4 h-4 mt-0.5 mr-2 text-yellow-400 flex-shrink-0" />
                  <span>Trade on crypto exchanges</span>
                </li>
                <li className="flex items-start">
                  <Zap className="w-4 h-4 mt-0.5 mr-2 text-yellow-400 flex-shrink-0" />
                  <span>Hold for value appreciation</span>
                </li>
                <li className="flex items-start">
                  <Zap className="w-4 h-4 mt-0.5 mr-2 text-yellow-400 flex-shrink-0" />
                  <span>Stake for additional rewards</span>
                </li>
              </ul>
            </div>
            
            {/* Phase 3: Real Value */}
            <div className="p-6 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm">
              <div className="flex items-center mb-3">
                <div className="p-2 rounded-lg bg-gradient-to-r from-green-400 to-teal-400">
                  <ShoppingCart className="w-6 h-6 text-white" />
                </div>
                <h4 className="ml-3 text-lg font-semibold">Phase 3: Real Value</h4>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mt-0.5 mr-2 text-green-400 flex-shrink-0" />
                  <span>Buy health devices & services</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mt-0.5 mr-2 text-green-400 flex-shrink-0" />
                  <span>Get 5-40% discounts based on level</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mt-0.5 mr-2 text-green-400 flex-shrink-0" />
                  <span>Pay for premium subscriptions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 mt-0.5 mr-2 text-green-400 flex-shrink-0" />
                  <span>Transfer to fiat currency</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Community Economy */}
          <div className="p-4 mt-6 bg-white bg-opacity-10 rounded-xl backdrop-blur-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h4 className="mb-1 text-lg font-semibold">🤝 Community Economy</h4>
                <p className="text-sm opacity-90">
                  Users with Community Titles can receive point donations from members they help. 
                  Build your reputation, help others, and earn real income!
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">1M+</p>
                  <p className="text-xs opacity-75">Points = Real Money</p>
                </div>
                <ChevronRight className="w-5 h-5" />
                <div className="text-center">
                  <p className="text-2xl font-bold">∞</p>
                  <p className="text-xs opacity-75">Earning Potential</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Game Changer Message - Enhanced Visibility */}
      <div className="relative p-8 overflow-hidden text-white shadow-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-black bg-opacity-10"></div>
        <div className="absolute top-0 right-0 w-40 h-40 transform translate-x-16 -translate-y-16 opacity-10">
          <div className="w-full h-full bg-white rounded-full"></div>
        </div>
        <div className="absolute bottom-0 left-0 w-32 h-32 transform -translate-x-12 translate-y-12 opacity-10">
          <div className="w-full h-full bg-white rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 bg-yellow-400 shadow-lg rounded-2xl">
              <span className="text-3xl">💡</span>
            </div>
            <div className="ml-6">
              <h2 className="text-3xl font-black tracking-tight">GAME CHANGER</h2>
              <p className="text-lg font-medium opacity-90">Revolutionary Healthcare Economics</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="p-6 bg-white bg-opacity-15 rounded-xl backdrop-blur-sm">
              <h3 className="mb-3 text-xl font-bold text-red-300">❌ Traditional Healthcare</h3>
              <p className="text-lg leading-relaxed">
                <strong>Costs you money</strong> when you're sick. The sicker you get, the more you pay.
              </p>
            </div>
            
            <div className="p-6 bg-white border-2 border-yellow-300 bg-opacity-20 rounded-xl backdrop-blur-sm">
              <h3 className="mb-3 text-xl font-bold text-green-300">✅ MediCureOn Revolution</h3>
              <p className="text-lg leading-relaxed">
                <strong>Pays YOU to stay healthy!</strong> The healthier you are, the wealthier you become.
              </p>
            </div>
          </div>
          
          <div className="p-6 mt-8 text-gray-900 bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 rounded-xl">
            <div className="text-center">
              <h3 className="mb-3 text-2xl font-black">🚀 YOUR EARNING POTENTIAL IS UNLIMITED</h3>
              <p className="mb-4 text-lg font-semibold">
                Earn points daily → Convert to MediCoin → Use for real products & services
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm font-medium">
                <span className="px-3 py-1 bg-white rounded-full bg-opacity-30">📊 Real-time rewards</span>
                <span className="px-3 py-1 bg-white rounded-full bg-opacity-30">💰 Cryptocurrency value</span>
                <span className="px-3 py-1 bg-white rounded-full bg-opacity-30">🛍️ Marketplace spending</span>
                <span className="px-3 py-1 bg-white rounded-full bg-opacity-30">💎 Investment potential</span>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* 💰 MEDICOIN (MCN) - CURRENCY OF THE FUTURE SHOWCASE */}
      <div className="p-6 mb-8 border-2 border-yellow-300 shadow-2xl bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 rounded-2xl backdrop-blur-sm">
        <div className="relative overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute w-32 h-32 bg-yellow-400 rounded-full animate-pulse top-4 right-4"></div>
            <div className="absolute w-20 h-20 bg-orange-400 rounded-full animate-bounce bottom-4 left-4"></div>
          </div>
          
          <div className="relative z-10">
            {/* Header with Animated Badges */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-3">
                <span className="text-4xl animate-bounce">💰</span>
                <h2 className="text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 bg-clip-text">
                  MediCoin (MCN) - Currency of the Future
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r from-green-500 to-green-600 animate-pulse">
                  ✨ UTILITY TOKEN
                </span>
                <span className="px-3 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r from-purple-500 to-purple-600 animate-pulse">
                  🚀 ICO LIVE
                </span>
                <span className="px-3 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r from-blue-500 to-blue-600 animate-pulse">
                  💎 6-132% STAKING
                </span>
              </div>
            </div>

            {/* Multi-Purpose Currency Grid */}
            <div className="grid gap-4 mb-6 md:grid-cols-2 lg:grid-cols-4">
              <div className="p-4 transition-all duration-300 bg-white border-2 border-yellow-200 shadow-lg rounded-xl hover:shadow-xl hover:scale-105">
                <div className="text-center">
                  <span className="block mb-2 text-3xl">🎯</span>
                  <h3 className="mb-2 font-bold text-yellow-700">Challenge Rewards</h3>
                  <p className="text-xs text-gray-600">Earn MCN tokens for completing health challenges</p>
                </div>
              </div>
              
              <div className="p-4 transition-all duration-300 bg-white border-2 border-orange-200 shadow-lg rounded-xl hover:shadow-xl hover:scale-105">
                <div className="text-center">
                  <span className="block mb-2 text-3xl">🔄</span>
                  <h3 className="mb-2 font-bold text-orange-700">Points Conversion</h3>
                  <p className="text-xs text-gray-600">Convert activity points to MediCoin instantly</p>
                </div>
              </div>
              
              <div className="p-4 transition-all duration-300 bg-white border-2 border-purple-200 shadow-lg rounded-xl hover:shadow-xl hover:scale-105">
                <div className="text-center">
                  <span className="block mb-2 text-3xl">💳</span>
                  <h3 className="mb-2 font-bold text-purple-700">Subscription-Based Discounts</h3>
                  <p className="text-xs text-gray-600">5-30% discounts on services based on your tier level</p>
                </div>
              </div>
              
              <div className="p-4 transition-all duration-300 bg-white border-2 border-green-200 shadow-lg rounded-xl hover:shadow-xl hover:scale-105">
                <div className="text-center">
                  <span className="block mb-2 text-3xl">🏥</span>
                  <h3 className="mb-2 font-bold text-green-700">Healthcare Payments</h3>
                  <p className="text-xs text-gray-600">Pay for medical services with MediCoin</p>
                </div>
              </div>
            </div>

            {/* Value Proposition Highlights */}
            <div className="grid gap-4 mb-6 md:grid-cols-3">
              <div className="p-4 border border-yellow-300 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-xl">
                <h4 className="mb-2 font-bold text-yellow-800">💰 Revolutionary Economics</h4>
                <ul className="space-y-1 text-xs text-yellow-700">
                  <li>• Deflationary token model</li>
                  <li>• Burn mechanism reduces supply</li>
                  <li>• Staking rewards up to 132% APY</li>
                  <li>• Limited total supply</li>
                </ul>
              </div>
              
              <div className="p-4 border border-purple-300 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl">
                <h4 className="mb-2 font-bold text-purple-800">🌐 Global Ecosystem</h4>
                <ul className="space-y-1 text-xs text-purple-700">
                  <li>• Healthcare provider network</li>
                  <li>• Pharmaceutical partnerships</li>
                  <li>• Insurance integrations</li>
                  <li>• Global marketplace access</li>
                </ul>
              </div>
              
              <div className="p-4 border border-green-300 bg-gradient-to-br from-green-100 to-teal-100 rounded-xl">
                <h4 className="mb-2 font-bold text-green-800">🚀 Future Technology</h4>
                <ul className="space-y-1 text-xs text-green-700">
                  <li>• AI-powered health insights</li>
                  <li>• Blockchain security</li>
                  <li>• Cross-chain compatibility</li>
                  <li>• Smart contract automation</li>
                </ul>
              </div>
            </div>

            {/* ICO Investment Opportunity */}
            <div className="p-6 mb-4 text-white bg-gradient-to-r from-red-500 via-pink-500 to-purple-600 rounded-2xl">
              <div className="text-center">
                <h3 className="mb-2 text-xl font-bold">🔥 LIMITED TIME ICO OPPORTUNITY</h3>
                <p className="mb-4 text-sm opacity-90">
                  Join the healthcare revolution! MediCoin ICO is LIVE with exclusive early investor benefits.
                </p>
                <div className="grid gap-3 mb-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="p-3 bg-white rounded-lg bg-opacity-20 backdrop-blur-sm">
                    <p className="text-xs opacity-80">1 Year Staking</p>
                    <p className="font-bold">6% APY</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg bg-opacity-20 backdrop-blur-sm">
                    <p className="text-xs opacity-80">2 Year Staking</p>
                    <p className="font-bold">24% APY</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg bg-opacity-20 backdrop-blur-sm">
                    <p className="text-xs opacity-80">3 Year Staking</p>
                    <p className="font-bold">84% APY</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg bg-opacity-20 backdrop-blur-sm">
                    <p className="text-xs opacity-80">5 Year Staking</p>
                    <p className="font-bold text-yellow-300">132% APY</p>
                  </div>
                </div>
                <a 
                  href="https://ico.medicureon.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3 font-bold text-purple-600 transition-all duration-300 transform bg-white rounded-full shadow-lg hover:bg-yellow-100 hover:scale-105"
                >
                  🚀 INVEST IN MEDICOIN NOW - ICO.MEDICUREON.COM
                </a>
              </div>
            </div>

            {/* Call to Action */}
            <div className="p-4 text-center bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl">
              <p className="font-bold text-white">
                💎 MediCoin: The only cryptocurrency designed specifically for healthcare. 
                <span className="block mt-1 text-sm opacity-90">
                  Earn rewards, save money, invest in the future of medicine!
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* MediCoin Promotion Banner */}
      <div className="p-6 text-white bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="mb-2 text-xl font-bold">💰 Convert to MediCoin!</h3>
            <p className="mb-4 opacity-90">
              Turn your health achievements into cryptocurrency. Every {mediCoinRate} points = 1 MediCoin!
            </p>
            <div className="flex items-center space-x-4 text-sm opacity-80">
              <span>• Real cryptocurrency value</span>
              <span>• Trade on exchanges</span>
              <span>• Exclusive holder benefits</span>
            </div>
          </div>
          <div className="text-center">
            <div className="mb-2 text-3xl font-bold">{getMediCoinEquivalent()}</div>
            <div className="mb-4 text-sm opacity-80">Available MediCoin</div>
            <button
              onClick={() => onNavigate('medicoin')}
              className="px-4 py-2 font-medium text-yellow-600 transition-colors bg-white rounded-lg hover:bg-yellow-50"
            >
              Learn More
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
